import { Button } from '@components/atoms/Button/Button';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Spinner } from '@components/atoms/Spinner/Spinner';
import { Title } from '@components/atoms/Title';
import { Navigation } from '@components/molecules/navigation/Navigation';
import { SocialShare } from '@components/molecules/SocialShare/SocialShare';
import { DefaultNoSpacing } from '@components/templates/layouts/DefaultNoSpacing';
import { Dialog, Transition } from '@headlessui/react';
import { extractSsrState } from '@lib/graphql/helpers/extractSsrState';
import { prerenderPaths } from '@lib/graphql/helpers/prerenderPaths';
import { IconArrow } from '@src/assets/icon-arrow';
import { IconClose } from '@src/assets/icon-close';
import { IconShare } from '@src/assets/icon-share';
import {
  GetBySlugFullDocument,
  type GetBySlugFullQuery,
  GetNavigationByHandleDocument,
  type GetNavigationByHandleQuery,
  GetShortsDocument,
} from '@src/lib/graphql/generated';
import { createUrqlClient, usePaginatedQuery } from '@src/lib/graphql/urql';
import { decodeOptions } from '@src/utils/urlHelpers';
import { AditionInitialization, AditionTagWithFallback } from '@vgn-medien-holding/vgn-fe-components';
import type { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { twMerge } from 'tailwind-merge';
import type { MenuItem } from 'types/menu';

const footerNavHandle = 'tv-media_footer_shorts';

const shortsQueryVariables = { page: 1, first: 15, onlyPublished: true };

/**
 * `tvmShorts` cannot be filtered by slug, so a deep link is resolved by paging
 * through the feed. Capped so an unknown slug never pulls in the whole archive.
 */
const maxDeepLinkPages = 5;

/** Slightly longer than vaul's 0.5s close animation, so snapping resumes on a settled DOM */
const drawerCloseDuration = 600;

// Shorts
const ad_settings_Shorts = {
  right1: {
    id: 5256655,
    name: 'TVM_Shorts_HPA_Cad',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_top: {
    id: 5256618,
    name: 'TVM_Mobile_Shorts_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

const ShortsPlayer = dynamic(() =>
  import('@components/molecules/ShortsPlayer/ShortsPlayer').then((mod) => mod.ShortsPlayer),
);

type Props = {
  data: GetBySlugFullQuery['findBySlug'];
  shortSlug?: string | null;
  footerNavigation: GetNavigationByHandleQuery['navigationByHandle'];
};

function Shorts({ data, shortSlug, footerNavigation }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [playerDrawerOpen, setPlayerDrawerOpen] = useState(false);
  const [activeShortIndex, setActiveShortIndex] = useState(-1);
  const [scrolling, setScrolling] = useState(false);
  const scrollingResetTimeout = useRef(null);
  const drawerCloseTimeout = useRef(null);
  const deepLinkPagesFetched = useRef(0);
  const deepLinkHandled = useRef(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const firstShortRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const [{ data: shortsData, hasMorePages, fetchNextPage }] = usePaginatedQuery({
    query: GetShortsDocument,
    variables: shortsQueryVariables,
  });

  const shorts = shortsData?.flatMap((data) => data?.tvmShorts?.data || []);
  const shortsCount = shorts?.length || 0;
  const deepLinkIndex = shortSlug
    ? shorts.findIndex((short) => short?.slug === shortSlug || short?.id === shortSlug)
    : -1;

  const openShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: 'TV-MEDIA Shorts',
          url: '/shorts',
        })
        .catch(() => undefined);
    } else {
      setDialogOpen(true);
    }
  };

  /**
   * Firefox re-snaps the feed whenever the drawer's DOM enters or leaves a snap child, so
   * snapping is suspended for the whole open/close cycle rather than just while it is open.
   */
  const handlePlayerDrawerOpenChange = useCallback((open: boolean) => {
    clearTimeout(drawerCloseTimeout.current);

    if (open) {
      setPlayerDrawerOpen(true);
      return;
    }
    drawerCloseTimeout.current = setTimeout(() => setPlayerDrawerOpen(false), drawerCloseDuration);
  }, []);

  const replaceUrl = ({ inView, url }: { inView: boolean; url: string }) => {
    if (inView && url !== router.asPath) {
      router.replace(url, undefined, { scroll: false, shallow: true });
    }
  };

  useEffect(() => {
    if (!shortSlug || deepLinkHandled.current) return;

    if (deepLinkIndex >= 0) {
      deepLinkHandled.current = true;
      setActiveShortIndex(deepLinkIndex);
      setScrolling(true);
      scrollingResetTimeout.current = setTimeout(() => setScrolling(false), 600);
      return;
    }

    if (!shortsCount) return;

    // Not in the pages loaded so far — keep paging until it shows up or the cap is hit
    if (hasMorePages && deepLinkPagesFetched.current < maxDeepLinkPages) {
      deepLinkPagesFetched.current += 1;
      fetchNextPage();
    } else {
      deepLinkHandled.current = true;
    }
  }, [shortSlug, deepLinkIndex, shortsCount, hasMorePages, fetchNextPage]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      clearTimeout(scrollingResetTimeout.current);
      clearTimeout(drawerCloseTimeout.current);
    };
  }, []);

  return (
    <div className="grid h-dvh grid-rows-[auto_1fr]">
      <AditionInitialization data={{ ad_settings: ad_settings_Shorts }} />
      <Head>{Meta(data?.__typename === 'Page' ? data?.metadata : {})}</Head>
      <Oewa pageKey="RedCont" categoryKey="KulturUndFreizeit" profileKey="FilmUndKino" />
      <div className="h-26"></div>
      <main
        ref={pageRef}
        className={twMerge(
          'scrollbar-none h-full overflow-y-auto overflow-x-hidden px-4',
          // Snapping stays off while a player drawer is open — Firefox re-snaps to the top of the
          // feed when the drawer mounts inside a snap child
          !scrolling && !playerDrawerOpen && 'snap-y snap-mandatory scroll-smooth',
        )}
      >
        <div className="radial-carousel-switcher-background snap-start snap-always pt-12 pb-20 md:pt-28 md:pb-44">
          <Title
            classProps={{
              heading: 'lg:text-6xl font-herokid leading-small lg:leading-header text-center text-5xl mb-6',
            }}
            level={1}
          >
            TV-MEDIA Shorts
          </Title>
          <p className="mx-auto mb-5 max-w-screen-md px-10 text-center text-lg lg:text-xl">
            Abonniere unsere TV-MEDIA Shorts und erhalten Filmrezensionen, Bestenlisten und Aktuelles direkt aus unserer
            Redaktion per E-Mail.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button title="Shorts Newsletter abonnieren" buttonStyle="primary" link="/newsletter-shorts" />
            <button type="button" onClick={openShare} className="button-icon">
              <IconShare />
            </button>
          </div>
        </div>
        {shorts.map((short, index, array) => {
          if (!short?.id || !short?.video?.url || !short?.title) return null;
          return (
            <Fragment key={short.id}>
              {index % 2 === 0 && index !== 0 && (
                <aside className="relative mx-auto my-6 grid aspect-short max-h-[62vh] snap-start snap-always place-items-center overflow-hidden rounded-xl bg-gray-800 md:max-h-[74vh]">
                  <AditionTagWithFallback
                    tag={ad_settings_Shorts.right1}
                    tagCount={index}
                    breakpoint={'lg'}
                    fallback={ad_settings_Shorts.mobile_top}
                  />
                </aside>
              )}
              <InView
                key={short.id}
                threshold={0.7}
                onChange={(inView) => {
                  replaceUrl({ inView, url: `/shorts/${short.slug || short.id}` });

                  // Only update on desktop since no buttons are visible on mobile
                  if (!scrolling && inView) {
                    setActiveShortIndex(index);
                  }
                  if (index === array.length - 1 && inView && hasMorePages) fetchNextPage();
                }}
              >
                {({ inView, ref }) => (
                  <ShortsPlayer
                    inView={inView}
                    src={short.video.url}
                    mime={short.video?.mime}
                    title={short.title}
                    description={short?.description}
                    author={short?.author}
                    entity={short?.entity}
                    isActive={activeShortIndex === index}
                    onDrawerOpenChange={handlePlayerDrawerOpenChange}
                  >
                    {/* InView Ref */}
                    <div ref={ref} className="pointer-events-none absolute inset-0" />
                    {/* Ref for scroll into view on page load */}
                    {index === 0 && <div ref={firstShortRef} className="pointer-events-none absolute inset-0" />}
                    {/* Navigation buttons for Desktop */}
                    <div
                      className="absolute inset-y-0 -left-16 hidden pointer:hidden flex-col justify-between md:flex"
                      style={{ display: !inView ? 'none' : undefined }}
                    >
                      <button
                        type="button"
                        disabled={scrolling}
                        onClick={() => {
                          clearTimeout(scrollingResetTimeout.current);
                          if (index <= 0) {
                            pageRef.current.scrollBy({ top: -500, left: 0, behavior: 'smooth' });
                          } else {
                            setActiveShortIndex(index - 1 < 0 ? 0 : index - 1);
                            setScrolling(true);
                            scrollingResetTimeout.current = setTimeout(() => setScrolling(false), 600);
                          }
                        }}
                      >
                        <IconArrow
                          classProps={{
                            root: 'cursor-pointer button-icon p-3 !size-12 -rotate-90',
                          }}
                        />
                      </button>
                      <button
                        type="button"
                        disabled={scrolling}
                        onClick={() => {
                          clearTimeout(scrollingResetTimeout.current);
                          setActiveShortIndex(index + 1 > shorts.length - 1 ? shorts.length : index + 1);
                          setScrolling(true);
                          scrollingResetTimeout.current = setTimeout(() => setScrolling(false), 600);
                        }}
                      >
                        <IconArrow
                          classProps={{
                            root: 'cursor-pointer button-icon p-3 !size-12 rotate-90',
                          }}
                        />
                      </button>
                    </div>
                  </ShortsPlayer>
                )}
              </InView>
            </Fragment>
          );
        })}
        <div className="flex h-1/5 w-full items-start justify-center">
          {hasMorePages && <Spinner iconSize="50px" />}
        </div>
      </main>
      <Transition appear show={dialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10 text-white" onClose={() => setDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md overflow-hidden rounded-xl bg-gray-700 align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="heading-4 mx-4 flex shrink-0 items-center justify-between border-gray-500 border-b py-6 font-medium text-2xl"
                  >
                    <span>Teilen</span>
                    <button type="button" onClick={() => setDialogOpen(false)}>
                      <IconClose />
                    </button>
                  </Dialog.Title>
                  <SocialShare />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      <Navigation
        classProps={{
          root: 'fixed bottom-0 z-50 left-0 right-0 md:right-auto py-4 px-8 lg:px-16 bg-black/80 md:rounded-tr-xl overflow-hidden',
          list: 'flex justify-center items-center md:justify-start gap-9 text-gray-400 font-semibold',
          link: 'underline decoration-transparent hover:decoration-primary underline-offset-4 transition-colors text-sm md:text-base duration-150',
        }}
        items={(footerNavigation?.items as MenuItem[]) || []}
      />
    </div>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: await prerenderPaths(['/shorts/index']),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { ssrQuery, ssrCache } = createUrqlClient();

  let data: GetBySlugFullQuery['findBySlug'] | null = null;
  const options = decodeOptions(params?.slug);
  const shortSlug = !options.slug || options.slug === 'index' ? null : options.slug.split('/')?.[0];

  try {
    const { data: entryData } = await ssrQuery({
      query: GetBySlugFullDocument,
      variables: {
        portal: 'tvmedia',
        slug: '/shorts',
      },
    });
    data = entryData?.findBySlug;
  } catch (err) {
    console.error(err);
    return { notFound: true };
  }

  if (!data) return { notFound: true };

  const { data: footerNavData } = await ssrQuery({
    query: GetNavigationByHandleDocument,
    variables: { handle: footerNavHandle },
  });

  await ssrQuery({
    query: GetShortsDocument,
    variables: shortsQueryVariables,
  });

  return {
    revalidate: 60,
    props: {
      data,
      shortSlug,
      footerNavigation: footerNavData?.navigationByHandle,
      urqlState: extractSsrState(ssrCache),
    },
  };
};

Shorts.PageLayout = ({ children }) => DefaultNoSpacing({ disableFooter: true, disableTopSpacing: true, children });

export default Shorts;
